import React from 'react'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import Badge from 'aldoo-ra/Badge'
import Tooltip from 'aldoo-ra/Tooltip'
import { DocumentTextIcon, EyeIcon, CodeBracketIcon } from '@heroicons/react/24/outline'

const TemplatesTab = ({ release, onPreview, onEditVariables }) => {
  const getTemplateStats = (templateId) => {
    if (!release.templateStats || release.templateStats.length === 0) return null
    return release.templateStats.find(stat => stat.templateId.toString() === templateId.toString())
  }

  if (!release.templates || release.templates.length === 0) {
    return (
      <div className="text-center py-8">
        <DocumentTextIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
        <Typography variant="admin_body1_dim">
          No templates configured for this release
        </Typography>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {release.templates.map((template, index) => {
          const stats = getTemplateStats(template.templateId)
          const sentCount = stats?.sent || 0

          const templateDelivered = stats?.delivered || 0
          const templateOpened = stats?.opened || 0
          const templateClicked = stats?.clicked || 0
          const templateBounced = stats?.bounced || 0
          const templateSpam = stats?.spamComplaint || 0
          const templateUnsub = stats?.unsubscribed || 0

          const deliveredRate = sentCount > 0 ? ((templateDelivered / sentCount) * 100).toFixed(1) : 0
          const openRate = sentCount > 0 ? ((templateOpened / sentCount) * 100).toFixed(1) : 0
          const clickRate = sentCount > 0 ? ((templateClicked / sentCount) * 100).toFixed(1) : 0
          const bounceRate = sentCount > 0 ? ((templateBounced / sentCount) * 100).toFixed(1) : 0
          const spamRate = sentCount > 0 ? ((templateSpam / sentCount) * 100).toFixed(1) : 0
          const unsubRate = sentCount > 0 ? ((templateUnsub / sentCount) * 100).toFixed(1) : 0

          return (
            <div key={index} className="border border-admin_bordered rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <Typography variant="admin_label" className="my-0">
                    {template.templateName && template.templateName !== 'Unknown Template'
                      ? template.templateName
                      : `Template ${template.templateId.slice(-8)}`}
                  </Typography>

                  {template.percentage && (
                    <Badge variant="admin_info" size="small">
                      {template.percentage}% traffic share
                    </Badge>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Tooltip text="Preview template" position="top">
                    <Button
                      variant="admin_icon"
                      onClick={() => onPreview(template.templateId)}
                    >
                      <EyeIcon className="w-4 h-4 text-admin_primary" />
                    </Button>
                  </Tooltip>

                  <Tooltip text="Edit variables" position="top">
                    <Button
                      variant="admin_icon"
                      onClick={() => onEditVariables(template)}
                    >
                      <CodeBracketIcon className="w-4 h-4 text-admin_primary" />
                    </Button>
                  </Tooltip>
                </div>
              </div>

              {/* Funnel Visualization */}
              {stats && sentCount > 0 && (
                <div className="space-y-4">
                  {/* Main funnel flow */}
                  <div className="flex items-center justify-center gap-3">
                    {/* Sent */}
                    <div className="flex flex-col items-center min-w-[80px]">
                      <Typography variant="admin_txt3_dim" className="mb-1">Sent</Typography>
                      <Typography variant="admin_h3" className="font-bold text-admin_text">
                        {sentCount.toLocaleString()}
                      </Typography>
                      <Typography variant="admin_txt3_dim">Emails</Typography>
                    </div>

                    <div className="flex items-center">
                      <svg width="40" height="24" viewBox="0 0 40 24" className="text-admin_bordered">
                        <path d="M0 12 L35 12 M35 12 L28 6 M35 12 L28 18" stroke="currentColor" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>

                    {/* Delivered */}
                    <div className="flex flex-col items-center min-w-[80px]">
                      <Typography variant="admin_txt3_dim" className="mb-1">Delivered</Typography>
                      <Typography variant="admin_h3" className="font-bold text-admin_success">
                        {templateDelivered.toLocaleString()}
                      </Typography>
                      <Typography variant="admin_txt3_dim" className="text-admin_success">
                        {deliveredRate}%
                      </Typography>
                    </div>

                    <div className="flex items-center">
                      <svg width="40" height="24" viewBox="0 0 40 24" className="text-admin_bordered">
                        <path d="M0 12 L35 12 M35 12 L28 6 M35 12 L28 18" stroke="currentColor" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>

                    {/* Opened */}
                    <div className="flex flex-col items-center min-w-[80px]">
                      <Typography variant="admin_txt3_dim" className="mb-1">Opened</Typography>
                      <Typography variant="admin_h3" className="font-bold text-admin_primary">
                        {templateOpened.toLocaleString()}
                      </Typography>
                      <Typography variant="admin_txt3_dim" className="text-admin_primary">
                        {openRate}%
                      </Typography>
                    </div>

                    <div className="flex items-center">
                      <svg width="40" height="24" viewBox="0 0 40 24" className="text-admin_bordered">
                        <path d="M0 12 L35 12 M35 12 L28 6 M35 12 L28 18" stroke="currentColor" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>

                    {/* Clicked */}
                    <div className="flex flex-col items-center min-w-[80px]">
                      <Typography variant="admin_txt3_dim" className="mb-1">Clicked</Typography>
                      <Typography variant="admin_h3" className="font-bold text-admin_info">
                        {templateClicked.toLocaleString()}
                      </Typography>
                      <Typography variant="admin_txt3_dim" className="text-admin_info">
                        {clickRate}%
                      </Typography>
                    </div>
                  </div>

                  {/* Negative outcomes below */}
                  <div className="flex items-center justify-center pt-3 border-t border-admin_bordered">
                    <div className="flex items-center justify-evenly w-full max-w-xl">
                      <div className="flex flex-col items-center min-w-[100px]">
                        <Typography variant="admin_txt3_dim" className="mb-1">Bounced</Typography>
                        <Typography variant="admin_h3" className="font-bold text-admin_warning">
                          {templateBounced.toLocaleString()}
                        </Typography>
                        <Typography variant="admin_txt3_dim" className="text-admin_warning">
                          {bounceRate}%
                        </Typography>
                      </div>

                      <div className="flex flex-col items-center min-w-[100px]">
                        <Typography variant="admin_txt3_dim" className="mb-1">Spam</Typography>
                        <Typography variant="admin_h3" className="font-bold text-admin_error">
                          {templateSpam.toLocaleString()}
                        </Typography>
                        <Typography variant="admin_txt3_dim" className="text-admin_error">
                          {spamRate}%
                        </Typography>
                      </div>

                      <div className="flex flex-col items-center min-w-[100px]">
                        <Typography variant="admin_txt3_dim" className="mb-1">Unsubscribed</Typography>
                        <Typography variant="admin_h3" className="font-bold text-admin_error">
                          {templateUnsub.toLocaleString()}
                        </Typography>
                        <Typography variant="admin_txt3_dim" className="text-admin_error">
                          {unsubRate}%
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default React.memo(TemplatesTab)
